import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import HumanCheck from '../components/HumanCheck';
import { createApiUrl } from '../utils/apiConfig';
import styles from './Register.module.css';
import { Link } from 'react-router-dom';

const RegisterStudent = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    contactName: '',
    email: '',
    teacherCode: '',
    yearGrade: '',
    parentEmail: '',
    parentPhone: '',
    dateOfBirth: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [teacherInfo, setTeacherInfo] = useState(null);
  const [humanCheckValid, setHumanCheckValid] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validateTeacherCode = async (teacherCode) => {
    try {
      if (!teacherCode.startsWith('TCH_') && !teacherCode.startsWith('teach_')) {
        setError('Invalid teacher code format. Please check with your teacher.');
        return false;
      }
      return true;
    } catch (err) {
      setError('Error validating teacher code. Please try again.');
      return false;
    }
  };

  const handleTeacherCodeBlur = async () => {
    if (formData.teacherCode) {
      const isValid = await validateTeacherCode(formData.teacherCode);
      if (isValid) {
        setTeacherInfo({
          teacherCode: formData.teacherCode,
          status: 'valid'
        });
        setError('');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validation (password will be auto-generated)

    if (!formData.teacherCode) {
      setError('Teacher Code is required. Please get the teacher code from your teacher.');
      setLoading(false);
      return;
    }

    if (!formData.yearGrade) {
      setError('Year Group is required');
      setLoading(false);
      return;
    }

    if (!formData.dateOfBirth) {
      setError('Date of Birth is required');
      setLoading(false);
      return;
    }

    if (!humanCheckValid) {
      setError('Please answer the human verification question correctly.');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(createApiUrl('/api/register/student'), {
        username: formData.email, // This will be overridden by backend with generated username
        contactName: formData.contactName,
        email: formData.email,
        teacherCode: formData.teacherCode,
        yearGrade: formData.yearGrade,
        parentEmail: formData.parentEmail,
        parentPhone: formData.parentPhone,
        dateOfBirth: formData.dateOfBirth
      });

      if (response.data.success) {
        const student = response.data.data.student;
        setSuccess(`Student registration successful!\n\nYour login credentials:\nUsername: ${student.username}\nPassword: ${student.password}\n\nPlease save these credentials. Redirecting to dashboard...`);
        
        // Store token and user data
        localStorage.setItem('token', response.data.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.data.student));
        
        // Redirect to student dashboard after 5 seconds to give time to read credentials
        setTimeout(() => {
          navigate('/student-dashboard');
        }, 5000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const grades = ['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

  return (
    <div className={styles.registerSchoolContainer}>
      <h1 className={styles.registerSchoolHeading}>Register<br />Student</h1>
      <div className={styles.registerSchoolCard}>
        {error && <div className="alert alert-danger">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}
        <form id="registerForm" onSubmit={handleSubmit} className={styles.registerForm}>
          <div className={styles.formField}>
            <input type="text" id="contactName" name="contactName" className={styles.formInput} required placeholder="Full Name" value={formData.contactName} onChange={handleChange} />
            <label htmlFor="contactName" className={styles.formLabel}>Full Name:</label>
          </div>
          <div className={styles.formField}>
            <input type="email" id="email" name="email" className={styles.formInput} required placeholder="Email" value={formData.email} onChange={handleChange} />
            <label htmlFor="email" className={styles.formLabel}>Email:</label>
          </div>
          <div className={styles.formField}>
            <input type="date" id="dateOfBirth" name="dateOfBirth" className={styles.formInput} required value={formData.dateOfBirth} onChange={handleChange} />
            <label htmlFor="dateOfBirth" className={styles.formLabel}>Date of Birth: *</label>
          </div>
          <div className={styles.formField}>
            <input type="text" id="teacherCode" name="teacherCode" className={styles.formInput} required placeholder="Teacher Code (Required)" value={formData.teacherCode} onChange={handleChange} onBlur={handleTeacherCodeBlur} />
            <label htmlFor="teacherCode" className={styles.formLabel}>Teacher Code: *</label>
          </div>
          {teacherInfo && (
            <div className="alert alert-info">
              <i className="fas fa-check-circle"></i>
              Teacher Code validated successfully
            </div>
          )}
          <div className={styles.formField}>
            <select id="yearGrade" name="yearGrade" className={styles.formSelect} value={formData.yearGrade} onChange={handleChange} required>
              <option value="">Select your grade</option>
              {grades.map(grade => (
                <option key={grade} value={grade}>
                  {grade === 'K' ? 'Reception/Kindergarten' : 
                   ['1', '2', '3', '4', '5', '6'].includes(grade) ? `Primary/Year ${grade}` : 
                   `Year/Grade ${grade}`}
                </option>
              ))}
            </select>
            <label htmlFor="yearGrade" className={styles.formLabel}>Year Group:</label>
          </div>
          <div className={styles.formField}>
            <input type="email" id="parentEmail" name="parentEmail" className={styles.formInput} placeholder="Parent Email" value={formData.parentEmail} onChange={handleChange} />
            <label htmlFor="parentEmail" className={styles.formLabel}>Parent Email:</label>
          </div>
          <div className={styles.formField}>
            <input type="tel" id="parentPhone" name="parentPhone" className={styles.formInput} placeholder="Parent Phone" value={formData.parentPhone} onChange={handleChange} />
            <label htmlFor="parentPhone" className={styles.formLabel}>Parent Phone:</label>
          </div>

          {/* Human Check */}
          <div className={styles.formField}>
            <HumanCheck 
              onValidationChange={setHumanCheckValid}
              error={error && !humanCheckValid ? 'Please answer the question correctly to proceed.' : ''}
              formType="registerStudent"
            />
          </div>

          <button type="submit" className={styles.submitButton} disabled={loading}>{loading ? 'Creating Account...' : 'Create Student Account'}</button>
        </form>

        <div className={styles.registerFooter}>
          <span>Already have an account? <Link to="/login">Login</Link></span>
          <p>
            Are you a teacher?{' '}
            <a href="/register/teacher">Teacher registration</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterStudent; 